/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBlockProspectNavQuery,
  EntWebsiteProspectNavCardFragment,
  EntWebsiteProspectNavFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import ContentfulButton from 'bundles/ent-website/contentful/core/ContentfulButton';
import Image from 'bundles/ent-website/contentful/core/Image';
import Markdown from 'bundles/ent-website/contentful/core/Markdown';
import { entWebsiteBlockProspectNavQuery } from 'bundles/ent-website/queries/EntWebsiteProspectNav';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsiteProspectNavFragment>;

type CardProps = {
  card: EntWebsiteProspectNavCardFragment;
  assembly: Props['assembly'];
  module: Props['content'];
};

function ProspectNavCard({ card, module }: CardProps) {
  const styles = {
    card: css`
      position: relative;
      width: 383px;
      max-width: 100%;
      min-height: 500px;
      background-color: var(--cds-color-grey-25);
      text-align: left;
      border-radius: 8px;
      overflow: hidden;
      margin-right: var(--cds-spacing-800);

      &:last-child {
        margin-right: 0;
      }

      ${breakpoints.down('sm')} {
        margin-right: var(--cds-spacing-300);
      }

      ${breakpoints.down('xs')} {
        width: 100%;
        min-height: 470px;
        margin-right: 0;
        margin-bottom: var(--cds-spacing-400);

        &:last-child {
          margin-bottom: 0;
        }
      }
    `,

    cardContent: css`
      padding: var(--cds-spacing-400);
      margin-bottom: var(--cds-spacing-800);

      ${breakpoints.down('sm')} {
        padding: var(--cds-spacing-200);
      }
    `,

    eyebrow: css`
      font-size: 14px !important;
      line-height: 150% !important;
      letter-spacing: -0.01rem;
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: var(--cds-spacing-200);
    `,

    cardHeadline: css`
      font-size: 1.5rem !important;
      line-height: 100% !important;
      letter-spacing: -0.03rem;
      margin-bottom: var(--cds-spacing-200);
    `,

    cardDescription: css`
      font-size: 1rem !important;
      line-height: 150% !important;
      letter-spacing: -0.03rem;
    `,

    cardCta: css`
      color: var(--cds-color-interactive-primary) !important;
      position: absolute;
      bottom: var(--cds-spacing-300);
      left: var(--cds-spacing-200);

      ${breakpoints.down('sm')} {
        bottom: var(--cds-spacing-100);
        left: 0;
      }
    `,
    // 16:9 aspect ratio
    cardImageCnt: css`
      position: relative;
      aspect-ratio: 16 / 9;
    `,
    cardImage: css`
      width: 100%;
      height: 100%;
    `,
  };
  return (
    <div css={styles.card}>
      {card.image && (
        <div css={styles.cardImageCnt}>
          <Image content={card.image} css={styles.cardImage} />
        </div>
      )}
      <div css={styles.cardContent}>
        <Typography2 component="p" css={styles.eyebrow}>
          {card.eyebrow}
        </Typography2>
        <Typography2 component="h3" variant="subtitleMedium" css={styles.cardHeadline}>
          {card.headline}
        </Typography2>
        <Typography2 component="p" css={styles.cardDescription}>
          {card.description}
        </Typography2>
      </div>
      {card.ctaTarget && card.ctaText && (
        <ContentfulButton
          module={module}
          position={0}
          variant="ghost"
          component="a"
          href={card.ctaTarget}
          icon={<ArrowNextIcon />}
          css={styles.cardCta}
          {...(card.shouldOpenTargetInNewWindow ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {card.ctaText}
        </ContentfulButton>
      )}
    </div>
  );
}

export function ProspectNav({ content, assembly }: Props) {
  const { visibility } = assembly;
  const styles = {
    content: css`
      /* stylelint-disable-line no-empty-source */
    `,

    eyebrow: css`
      font-size: 20px !important;
      line-height: 150%;
      letter-spacing: -0.03em;
      font-weight: 600;
      text-transform: uppercase;
      margin-bottom: var(--cds-spacing-300);

      ${breakpoints.down('xs')} {
        font-size: 14px !important;
      }
    `,

    headline: css`
      font-size: 3rem !important;
      letter-spacing: -0.03rem;
      line-height: 100% !important;
      margin-bottom: var(--cds-spacing-600);

      ${breakpoints.down('sm')} {
        font-size: 2.5rem !important;
      }

      ${breakpoints.down('xs')} {
        font-size: 2.5rem !important;
      }
    `,

    description: css`
      p {
        font-size: 20px !important;
        line-height: 150% !important;
        letter-spacing: -0.02em;

        ${breakpoints.down('xs')} {
          font-size: 16px !important;
          letter-spacing: -0.03em;
        }
      }

      ${breakpoints.down('xs')} {
        margin-bottom: var(--cds-spacing-400);
      }

      .rc-Markdown.styled li {
        font-size: 20px !important;
      }
    `,

    cards: css`
      display: flex;
      flex-direction: row;

      ${breakpoints.down('xs')} {
        flex-direction: column;
      }
    `,
  };

  const contentPanel = () => (
    <div css={styles.content}>
      {assembly.eyebrow && (
        <Typography2 component="p" variant="bodyPrimary" css={styles.eyebrow} color="inherit">
          {assembly.eyebrow}
        </Typography2>
      )}
      {content.headline && visibility.headline && (
        <Typography variant="d2semibold" color="inherit" css={styles.headline}>
          {content.headline}
        </Typography>
      )}
      {content.description && visibility.description && (
        <Typography2 variant="bodyPrimary" component="div" color="inherit" css={styles.description}>
          <Markdown source={content.description} module={content} />
        </Typography2>
      )}
    </div>
  );

  const cardsPanel = () => (
    <div css={styles.cards}>
      {content.cardsCollection?.items.map(
        (card) => card && <ProspectNavCard card={card} assembly={assembly} key={card.sys.id} module={content} />
      )}
    </div>
  );
  return <BlockLayout assembly={assembly} renderContent={contentPanel} renderImage={cardsPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockProspectNav = withBlockQuery<EntWebsiteProspectNavFragment, EntWebsiteBlockProspectNavQuery>(
  entWebsiteBlockProspectNavQuery
)(ProspectNav);
